import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getInviteInfo, getGitHubAuthUrl } from "../api";
import GitHubIcon from "../components/GitHubIcon";

/**
 * Invite entry point: /join/:code
 *
 * An invited teammate must NOT be sent through the GitHub App install flow —
 * whoever set up the team already installed it, and sending them to the
 * install URL lands them on GitHub's configure screen with no way back. This
 * screen sends them to the authorize URL instead, once they choose to
 * continue.
 *
 * The code rides through GitHub's OAuth `state` param (see getGitHubAuthUrl
 * below) so auth-callback can verify real org access — via the same
 * GET /user/installations check already used for install-flow hijack
 * prevention — while a live user token still exists, then perform the join
 * itself server-side (item 15). It's also stashed in sessionStorage
 * (rememberJoinCode) as a UI-only fallback: it pre-fills Onboarding's join
 * tab if the OAuth-verified join fails, and is what the legacy ?join=CODE
 * query form converges through. It no longer performs the join on its own.
 *
 * Team name comes from a public, unauthenticated lookup (GET
 * /api/teams/invite/:code) — the invite code itself (192-bit random token)
 * remains the real access boundary; this endpoint only ever returns a name,
 * never the code back out.
 */
export default function JoinPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, rememberJoinCode } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | valid | invalid
  const [teamName, setTeamName] = useState(null);

  useEffect(() => {
    if (authLoading) return;

    rememberJoinCode(code);

    // Already signed in — skip straight to the (pre-filled) join form on
    // /dashboard rather than showing an intermediate screen they don't need.
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
      return;
    }

    let cancelled = false;
    getInviteInfo(code)
      .then((res) => {
        if (cancelled) return;
        setTeamName(res.data?.data?.team_name || null);
        setStatus("valid");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("invalid");
      });

    return () => {
      cancelled = true;
    };
  }, [code, isAuthenticated, authLoading, navigate, rememberJoinCode]);

  if (authLoading || status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-rf-bg-primary">
        <div className="text-sm text-rf-text-secondary">Opening your invite...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-rf-bg-primary px-4">
      <div className="w-full max-w-md rounded-2xl border border-rf-border bg-rf-bg-secondary p-10 text-center">
        <div className="mb-8 flex items-center justify-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-rf-accent to-rf-accent-blue text-lg font-bold text-white">
            A
          </div>
          <span className="text-2xl font-bold tracking-tight text-rf-text-primary">
            Anthurium
          </span>
        </div>

        {status === "valid" ? (
          <>
            <h1 className="mb-2 text-xl font-semibold text-rf-text-primary">
              You've been invited to{" "}
              {teamName ? <span className="text-rf-accent">{teamName}</span> : "a team"}
            </h1>
            <p className="mb-8 text-sm leading-relaxed text-rf-text-secondary">
              Sign in with GitHub to join — no installation needed, your team
              already connected its repos.
            </p>
            <button
              onClick={() => {
                window.location.href = getGitHubAuthUrl(code);
              }}
              className="flex w-full items-center justify-center gap-3 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-all duration-200 hover:bg-gray-100"
            >
              <GitHubIcon />
              Continue with GitHub
            </button>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-xl font-semibold text-rf-text-primary">
              This invite link isn't valid
            </h1>
            <p className="mb-8 text-sm leading-relaxed text-rf-text-secondary">
              It may have been rotated by the team's admin. Ask them for a
              fresh invite link.
            </p>
            <Link
              to="/"
              className="cursor-pointer text-sm font-medium text-rf-accent-blue transition-colors duration-200 hover:text-rf-text-primary"
            >
              ← Back to Anthurium
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
